/**
 * Язык перевода редактора эфира, когда в адресе его нет.
 *
 * Проба 20261103-P50m10c: адрес https://tts-audio.dev.torsunov.pro/fast/<медиа>?folder=1 без `&lang=en` открывал
 * только русский столбец — ни перевода, ни кнопки ♪; переводчик полторы минуты искал, что дописать в адрес. У эфира
 * язык перевода известен (`realtime_editor/status`: `target_languages`, `language_from`): он один — редактор берёт его
 * сам (адрес дописывается `&lang=…`, как будто его так и открыли). Языков несколько — выбирать переводчику.
 */
export interface StreamLangStatus {
    is_realtime_stream?: boolean;
    language_from?: string;
    target_languages?: string[];
}

/** Язык из адреса: `zh` в адресе, `ch` в редакторе (как в `Audios`). */
export const urlLangOf = (search: string): string | null => {
    const lang = new URLSearchParams(search).get("lang");
    return lang ? (lang === "zh" ? "ch" : lang) : null;
};

/** Язык перевода, который редактор эфира берёт сам: только /fast/, только без `lang` в адресе, только у эфира с
 *  ОДНИМ языком перевода. Иначе null. */
export const autoStreamLang = (
    pathname: string,
    search: string,
    status: StreamLangStatus | null | undefined,
    originalLang?: string,
): string | null => {
    if (!pathname.startsWith("/fast/")) return null;
    if (new URLSearchParams(search).get("lang")) return null;
    if (!status?.is_realtime_stream) return null;
    const source = String(status.language_from || originalLang || "ru").split("-")[0].toLowerCase();
    const langs = Array.from(
        new Set(
            (status.target_languages || [])
                .map((one) => String(one || "").split("-")[0].toLowerCase())
                .filter((one) => one && one !== source),
        ),
    );
    return langs.length === 1 ? langs[0] : null;
};

/** Тот же адрес с языком перевода. */
export const searchWithLang = (search: string, lang: string): string => {
    const params = new URLSearchParams(search);
    params.set("lang", lang);
    return `?${params.toString()}`;
};

/**
 * Какую папку отпустить при смене папки/языка (CONTRACTS п.15: уход с папки — её правки в эфир сразу). Отпускается
 * только папка ЯЗЫКА ПЕРЕВОДА: у русского (столбец без языка перевода) заявок правок нет, и `release` с `lang: "ru"`
 * сервер отвергает 422 «lang — язык перевода этого эфира» (проба P50m10c: заход на ?folder=1&lang=en после
 * ?folder=1 без языка сам слал release {folder_num: 1, lang: "ru"}).
 */
export const folderToRelease = (
    prev: { folder: number; lang: string } | null,
    current: { folder: number; lang: string },
    originalLang?: string,
): { folder: number; lang: string } | null => {
    if (!prev || !prev.folder || !prev.lang) return null;
    if (prev.folder === current.folder && prev.lang === current.lang) return null;
    const source = String(originalLang || "ru").toLowerCase();
    if (String(prev.lang).toLowerCase() === source) return null;
    return prev;
};
